/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: English
 *
 * ## Style Direction (from analysis)
 * <style-name>AuraGlass</style-name>
 * **Mode:** Dark
 * 
 * **Style Recipe:**
 * - **Core Visual Style:** Aurora / Holographic Glass
 * - **Layout Structure:** Floating / Borderless
 * - **Visual Complexity:** Rich / Expressive
 * 
 * **Implementation Techniques:**
 * - **Material & Surfaces:** Translucent panels (`backdrop-filter: blur(24px)`) layered over a deep, breathing background. Heavy use of subtle noise/grain texture on the background to prevent gradient banding and add a premium matte finish.
 * - **Border-Radius:** 16px to 24px for large panels, creating a smooth, organic flow.
 * - **Typography:** A sleek, modern sans-serif (e.g., Roobert or Manrope) with a slightly tracked-out feel for headers. Code blocks use a clean, glowing monospace.
 * - **Lighting & Shadows:** Inner specular highlights (top and left edges of the glass panels using `box-shadow: inset 1px 1px 0px rgba(255,255,255,0.1)`) to simulate the physical thickness of the glass. Soft, expansive outer drop shadows to separate floating panels from the background.
 * - **Border Treatments:** Zero solid borders; edges are entirely defined by the contrast between the blur, the inner highlight, and the background gradients.
 * 
 * **Color Direction:**
 * An Obsidian/Deep Space background. The Aurora effect uses fluid, slow-moving mesh gradients blending Emerald Green, Deep Ocean Blue, and Amethyst Purple. These colors bleed into the translucent surfaces. Buttons and active states utilize a soft white glow (bloom) rather than flat background fills.
 * ============================================================
 */

import React, { useState } from 'react';
import { Icon } from '@iconify/react';

export default function StylePreview() {
  const [activeTab, setActiveTab] = useState('design');

  const componentData = {
    name: 'Primary Button',
    status: 'Published',
    version: 'v1.2.0',
    description: 'The primary action button used for main user interactions across the application.',
  };

  const propsData = [
    { name: 'variant', type: "'primary' | 'secondary' | 'danger'", default: "'primary'", description: 'The visual style of the button.' },
    { name: 'size', type: "'sm' | 'md' | 'lg'", default: "'md'", description: 'The size of the button.' },
    { name: 'disabled', type: 'boolean', default: 'false', description: 'Whether the button is disabled.' },
    { name: 'onClick', type: '() => void', default: 'undefined', description: 'Callback function when clicked.' },
  ];

  const codeSnippet = `
import React from 'react';
import { Button } from '@/components/ui/button';

export function Example() {
  return (
    <Button variant="primary" size="md" onClick={() => console.log('clicked')}>
      Click Me
    </Button>
  );
}
  `.trim();

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-slate-200 font-sans relative overflow-hidden flex">
      {/* Aurora Background Effects */}
      <div className="absolute top-0 left-1/4 w-[800px] h-[600px] bg-emerald-600/20 rounded-full blur-[120px] mix-blend-screen pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] mix-blend-screen pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] bg-purple-600/10 rounded-full blur-[140px] mix-blend-screen pointer-events-none" />
      
      {/* Noise Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")' }}
      />

      {/* Sidebar Navigation (Glassmorphism Floating) */}
      <div className="w-64 flex-shrink-0 p-6 flex flex-col gap-6 z-10">
        <div className="flex items-center gap-3 px-2 text-white">
          <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center backdrop-blur-md shadow-[inset_1px_1px_0px_rgba(255,255,255,0.2)]">
            <Icon icon="lucide:layers" className="text-xl text-emerald-300" />
          </div>
          <span className="font-semibold tracking-wide">ComponentHub</span>
        </div>

        <nav className="flex flex-col gap-2 flex-1">
          {[
            { id: 'dashboard', icon: 'lucide:layout-dashboard', label: 'Dashboard' },
            { id: 'components', icon: 'lucide:grid', label: 'Components', active: true },
            { id: 'tokens', icon: 'lucide:palette', label: 'Design Tokens' },
            { id: 'changelog', icon: 'lucide:activity', label: 'Changelog' },
          ].map((item) => (
            <button
              key={item.id}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 ${
                item.active 
                  ? 'bg-white/10 text-white shadow-[inset_1px_1px_0px_rgba(255,255,255,0.15)] backdrop-blur-md shadow-lg shadow-black/20' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon icon={item.icon} className="text-lg" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="mt-auto flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 backdrop-blur-md shadow-[inset_1px_1px_0px_rgba(255,255,255,0.05)]">
          <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-emerald-400 to-blue-500 p-[2px]">
            <div className="w-full h-full bg-black/50 rounded-full flex items-center justify-center backdrop-blur-sm">
              <span className="text-xs font-bold text-white">JD</span>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-white">Jane Doe</span>
            <span className="text-xs text-slate-400">UI Designer</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col p-6 pl-0 z-10 h-screen overflow-hidden">
        {/* Main Floating Panel */}
        <div className="flex-1 flex flex-col bg-white/[0.03] backdrop-blur-[24px] rounded-3xl shadow-[inset_1px_1px_0px_rgba(255,255,255,0.1)] overflow-hidden shadow-2xl shadow-black/50">
          
          {/* Header */}
          <header className="flex items-center justify-between px-8 py-6 border-b border-white/5">
            <div className="flex items-center gap-4">
              <button className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors shadow-[inset_1px_1px_0px_rgba(255,255,255,0.1)]">
                <Icon icon="lucide:arrow-left" className="text-xl text-slate-300" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold text-white tracking-tight">{componentData.name}</h1>
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-500/20 text-emerald-300 shadow-[inset_1px_1px_0px_rgba(255,255,255,0.1)] border border-emerald-500/20">
                    {componentData.status}
                  </span>
                  <span className="text-sm text-slate-400 font-mono bg-white/5 px-2 py-1 rounded-md">{componentData.version}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all shadow-[inset_1px_1px_0px_rgba(255,255,255,0.1)]">
                <Icon icon="lucide:history" />
                <span className="font-medium text-sm">Versions</span>
              </button>
              <button className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-500/80 to-blue-500/80 hover:from-emerald-400/90 hover:to-blue-400/90 text-white transition-all shadow-[inset_1px_1px_0px_rgba(255,255,255,0.2),0_0_20px_rgba(52,211,153,0.3)] backdrop-blur-md">
                <Icon icon="lucide:play" />
                <span className="font-medium text-sm">Preview</span>
              </button>
              <button className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors shadow-[inset_1px_1px_0px_rgba(255,255,255,0.1)] ml-1">
                <Icon icon="lucide:more-vertical" className="text-slate-300" />
              </button>
            </div>
          </header>

          {/* Tabs */}
          <div className="px-8 pt-6 flex gap-8 border-b border-white/5 relative">
            {['design', 'code', 'props', 'guidelines'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 text-sm font-medium tracking-wide transition-colors relative capitalize ${
                  activeTab === tab ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {tab}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-emerald-400 to-blue-400 shadow-[0_-2px_10px_rgba(52,211,153,0.5)] rounded-t-full" />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content Area */}
          <div className="flex-1 overflow-auto p-8 relative">
            {activeTab === 'design' && (
              <div className="flex flex-col gap-6 h-full">
                <div className="flex-1 rounded-2xl bg-black/20 flex items-center justify-center border border-white/5 shadow-[inset_0_0_40px_rgba(0,0,0,0.5)] relative overflow-hidden group">
                   {/* Mock rendered component in design view */}
                   <button className="px-6 py-3 rounded-xl bg-emerald-500 text-white font-medium shadow-[inset_1px_1px_0px_rgba(255,255,255,0.3),0_0_20px_rgba(16,185,129,0.4)] transition-transform hover:scale-105 active:scale-95">
                     Primary Action
                   </button>
                   
                   <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                     <button className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 text-white shadow-[inset_1px_1px_0px_rgba(255,255,255,0.1)]">
                       <Icon icon="lucide:maximize-2" className="text-base" />
                     </button>
                   </div>
                </div>
              </div>
            )}

            {activeTab === 'code' && (
              <div className="h-full rounded-2xl bg-[#0d0d12]/80 backdrop-blur-md border border-white/5 p-6 shadow-[inset_1px_1px_0px_rgba(255,255,255,0.05)] overflow-auto">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono text-slate-400">Example.tsx</span>
                  <button className="flex items-center gap-2 text-xs text-slate-300 hover:text-white bg-white/5 px-3 py-1.5 rounded-lg transition-colors">
                    <Icon icon="lucide:copy" className="text-sm" />
                    Copy
                  </button>
                </div>
                <pre className="text-sm font-mono text-emerald-200/90 whitespace-pre-wrap leading-relaxed">
                  <code dangerouslySetInnerHTML={{ __html: codeSnippet.replace(/([A-Z][a-z]+)/g, '<span class="text-blue-300">$1</span>').replace(/("[^"]*")/g, '<span class="text-emerald-400">$1</span>') }} />
                </pre>
              </div>
            )}

            {activeTab === 'props' && (
              <div className="rounded-2xl border border-white/5 overflow-hidden bg-white/[0.02]">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/5 bg-white/5 text-slate-300 text-sm">
                      <th className="px-6 py-4 font-medium">Name</th>
                      <th className="px-6 py-4 font-medium">Type</th>
                      <th className="px-6 py-4 font-medium">Default</th>
                      <th className="px-6 py-4 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {propsData.map((prop, idx) => (
                      <tr key={idx} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-emerald-300 bg-emerald-500/10 px-2 py-1 rounded-md">{prop.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-blue-300">{prop.type}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-purple-300 bg-purple-500/10 px-2 py-1 rounded-md">{prop.default}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-400">{prop.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'guidelines' && (
              <div className="prose prose-invert prose-emerald max-w-none">
                <h3 className="text-xl font-medium text-white mb-4">Usage Guidelines</h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  {componentData.description} Primary buttons should be used for the main action on a page. Avoid using multiple primary buttons on a single screen to prevent user confusion.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2 text-emerald-400 mb-3 font-medium">
                      <Icon icon="lucide:check-circle" /> Do
                    </div>
                    <p className="text-sm text-emerald-200/80">Use for the primary action on a page (e.g., Save, Submit, Create).</p>
                  </div>
                  <div className="p-5 rounded-2xl bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2 text-red-400 mb-3 font-medium">
                      <Icon icon="lucide:x-circle" /> Don't
                    </div>
                    <p className="text-sm text-red-200/80">Use multiple primary buttons in the same button group or section.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
